import React from 'react';
import { Row, Col, Form } from 'react-bootstrap';
import { FaInfoCircle, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import './GroupConfigurationSection.scss';

interface GroupConfigurationSectionProps {
  groupConfiguration: {
    min_variance: number;
    max_variance: number;
    min_selections: number;
    max_selections: number;
    is_price_modifier: boolean;
  };
  setGroupConfiguration: React.Dispatch<React.SetStateAction<{
    min_variance: number;
    max_variance: number;
    min_selections: number;
    max_selections: number;
    is_price_modifier: boolean;
  }>>;
  selectedGroupName: string;
}

const GroupConfigurationSection: React.FC<GroupConfigurationSectionProps> = ({
  groupConfiguration,
  setGroupConfiguration,
  selectedGroupName
}) => {
  // Increment/Decrement handlers with enhanced validation
  const handleIncrement = (type: 'min_variance' | 'max_variance' | 'min_selections' | 'max_selections') => {
    setGroupConfiguration(prev => {
      const newConfig = { ...prev };
      
      switch (type) {
        case 'min_variance':
          // Variance min can never exceed selections max or variance max
          if (prev.min_variance < prev.max_variance && prev.min_variance < prev.max_selections) {
            newConfig.min_variance = prev.min_variance + 1;
          }
          break;
        case 'max_variance':
          // Variance max can never exceed selections max
          if (prev.max_variance < prev.max_selections) {
            newConfig.max_variance = prev.max_variance + 1;
          }
          break;
        case 'min_selections':
          if (prev.min_selections < prev.max_selections) {
            newConfig.min_selections = prev.min_selections + 1;
          }
          break;
        case 'max_selections':
          newConfig.max_selections = prev.max_selections + 1;
          break;
      }
      
      return newConfig;
    });
  };

  const handleDecrement = (type: 'min_variance' | 'max_variance' | 'min_selections' | 'max_selections') => {
    setGroupConfiguration(prev => {
      const newConfig = { ...prev };
      
      switch (type) {
        case 'min_variance':
          if (prev.min_variance > 0) {
            newConfig.min_variance = prev.min_variance - 1;
          }
          break;
        case 'max_variance':
          if (prev.max_variance > prev.min_variance) {
            newConfig.max_variance = prev.max_variance - 1;
          }
          break;
        case 'min_selections':
          if (prev.min_selections > 0) {
            newConfig.min_selections = prev.min_selections - 1;
          }
          break;
        case 'max_selections':
          if (prev.max_selections > prev.min_selections) {
            // When reducing max selections, also adjust variance if needed
            const newMaxSelections = prev.max_selections - 1;
            newConfig.max_selections = newMaxSelections;
            
            // Ensure variance doesn't exceed new selection limits
            if (prev.max_variance > newMaxSelections) {
              newConfig.max_variance = newMaxSelections;
            }
            if (prev.min_variance > newMaxSelections) {
              newConfig.min_variance = Math.min(prev.min_variance, newMaxSelections);
            }
          }
          break;
      }
      
      return newConfig;
    });
  };

  // Generate configuration explanation
  const generateConfigurationExplanation = () => {
    let explanation = "Con esta configuración, ";
    
    // Selection rules
    if (groupConfiguration.min_selections === 0 && groupConfiguration.max_selections === 1) {
      explanation += "elegir para tu cliente será opcional, y podría elegir hasta 1 producto";
    } else if (groupConfiguration.min_selections === 0) {
      explanation += `elegir para tu cliente será opcional, y podría elegir hasta ${groupConfiguration.max_selections} producto${groupConfiguration.max_selections !== 1 ? 's' : ''}`;
    } else if (groupConfiguration.min_selections === groupConfiguration.max_selections) {
      explanation += `tu cliente tendrá que elegir exactamente ${groupConfiguration.min_selections} producto${groupConfiguration.min_selections !== 1 ? 's' : ''}`;
    } else {
      explanation += `tu cliente tendrá que elegir como mínimo ${groupConfiguration.min_selections} producto${groupConfiguration.min_selections !== 1 ? 's' : ''}, y podría elegir hasta ${groupConfiguration.max_selections} producto${groupConfiguration.max_selections !== 1 ? 's' : ''}`;
    }
    
    explanation += ". ";
    
    // Variance rules
    if (groupConfiguration.min_variance <= 1 && groupConfiguration.max_variance <= 1) {
      explanation += "Puede elegir productos repetidos sin restricción.";
    } else if (groupConfiguration.min_variance === groupConfiguration.max_variance) {
      explanation += `Además, tendría que elegir exactamente ${groupConfiguration.max_variance} productos diferentes (no puede repetir).`;
    } else if (groupConfiguration.min_variance <= 1) {
      explanation += `Además, podría elegir hasta ${groupConfiguration.max_variance} productos diferentes como máximo.`;
    } else {
      explanation += `Además, tendría que elegir como mínimo ${groupConfiguration.min_variance} productos diferentes, hasta ${groupConfiguration.max_variance} como máximo.`;
    }

    return explanation;
  };

  // Number input with side arrows component
  const NumberInputWithSideArrows = ({ 
    value, 
    onIncrement, 
    onDecrement, 
    label, 
    min = 0,
    canIncrement = true,
    canDecrement = true 
  }: {
    value: number;
    onIncrement: () => void;
    onDecrement: () => void;
    label: string;
    min?: number;
    canIncrement?: boolean;
    canDecrement?: boolean;
  }) => (
    <div className="number-input-with-side-arrows">
      <Form.Label className="clean-label">{label}</Form.Label>
      <div className="input-with-side-controls">
        <button
          type="button"
          className="side-control-btn decrement"
          onClick={onDecrement}
          disabled={!canDecrement}
        >
          <FaChevronDown />
        </button>
        <Form.Control
          type="number"
          value={value}
          onChange={() => {}} // Controlled by increment/decrement only
          className="clean-number-input"
          min={min}
          readOnly
        />
        <button
          type="button"
          className="side-control-btn increment"
          onClick={onIncrement}
          disabled={!canIncrement}
        >
          <FaChevronUp />
        </button>
      </div>
    </div>
  );

  return (
    <div className="existing-group-config-section-clean">
      <h6 className="config-title-clean">Configuración de Asociación</h6>
      
      {/* Configuration Grid */}
      <Row className="configuration-grid">
        <Col md={3}>
          <NumberInputWithSideArrows
            value={groupConfiguration.min_variance}
            onIncrement={() => handleIncrement('min_variance')}
            onDecrement={() => handleDecrement('min_variance')}
            label="Variación Mínima"
            min={0}
            canIncrement={groupConfiguration.min_variance < groupConfiguration.max_variance && groupConfiguration.min_variance < groupConfiguration.max_selections}
            canDecrement={groupConfiguration.min_variance > 0}
          />
        </Col>
        
        <Col md={3}>
          <NumberInputWithSideArrows
            value={groupConfiguration.max_variance}
            onIncrement={() => handleIncrement('max_variance')}
            onDecrement={() => handleDecrement('max_variance')}
            label="Variación Máxima"
            min={0}
            canIncrement={groupConfiguration.max_variance < groupConfiguration.max_selections}
            canDecrement={groupConfiguration.max_variance > groupConfiguration.min_variance}
          />
        </Col>

        <Col md={3}>
          <NumberInputWithSideArrows
            value={groupConfiguration.min_selections}
            onIncrement={() => handleIncrement('min_selections')}
            onDecrement={() => handleDecrement('min_selections')}
            label="Selecciones Mínimas"
            min={0}
            canIncrement={groupConfiguration.min_selections < groupConfiguration.max_selections}
            canDecrement={groupConfiguration.min_selections > 0}
          />
        </Col>
        
        <Col md={3}>
          <NumberInputWithSideArrows
            value={groupConfiguration.max_selections}
            onIncrement={() => handleIncrement('max_selections')}
            onDecrement={() => handleDecrement('max_selections')}
            label="Selecciones Máximas"
            min={1}
            canDecrement={groupConfiguration.max_selections > groupConfiguration.min_selections}
          />
        </Col>
      </Row>

      {/* Price Modifier Checkbox */}
      <div className="price-modifier-section">
        <Form.Check
          type="checkbox"
          id="price-modifier-check"
          label="Modificar precio base del ítem"
          checked={groupConfiguration.is_price_modifier}
          onChange={(e) => setGroupConfiguration(prev => ({
            ...prev,
            is_price_modifier: e.target.checked
          }))}
          className="price-modifier-checkbox-clean"
        />
        <small className="price-modifier-help-clean">
          Si está activo, las selecciones de este grupo modificarán el precio base del ítem
        </small>
      </div>

      {/* Configuration Explanation Banner */}
      <div className="configuration-banner-clean">
        <FaInfoCircle className="banner-icon-clean" />
        <span className="banner-text-clean">{generateConfigurationExplanation()}</span>
      </div>
    </div>
  );
};

export default GroupConfigurationSection;
